import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron.svg';
import { clsx } from 'clsx';

import { getOwnershipContagionLevel } from '../utils/monitoringResultsUtils';

import type IMonitoringOwnershipEntityCard from './interfaces/IMonitoringOwnershipEntityCard';

import styles from './sass/MonitoringOwnershipEntityCard.module.scss';

const SCORE_CLASS_BY_LEVEL: Record<'low' | 'mid' | 'high', string> = {
  low: 'scoreLow',
  mid: 'scoreMid',
  high: 'scoreHigh',
};

function MonitoringOwnershipEntityCard({
  entity,
  defaultExpanded = getOwnershipContagionLevel(entity.contagion_score) === 'high',
}: IMonitoringOwnershipEntityCard): React.JSX.Element {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);
  const profile = entity.ownership_profile;
  const scoreLevel = getOwnershipContagionLevel(entity.contagion_score);
  const trendKey = entity.trend === 'declining' || entity.trend === 'rising' || entity.trend === 'stable'
    ? entity.trend
    : 'stable';
  const groupEntities = profile.group_entities_preview || [];

  return (
    <article className={styles.container}>
      <button
        type="button"
        className={styles.header}
        onClick={() => setIsExpanded((prevExpanded) => !prevExpanded)}
        aria-expanded={isExpanded}
      >
        <div className={styles.headerMain}>
          <h3 className={styles.title}>{entity.name}</h3>

          <div className={styles.badgeRow}>
            <span className={clsx(styles.badge, styles[SCORE_CLASS_BY_LEVEL[scoreLevel]])}>
              {t('valueChainMap.monitoring.results.ownershipContagionScore', {
                value: entity.contagion_score,
              })}
            </span>
            <span className={clsx(styles.badge, styles[`trend_${trendKey}`])}>
              {t(`valueChainMap.monitoring.results.ownershipTrend.${trendKey}`)}
            </span>
            {entity.spike ? (
              <span className={clsx(styles.badge, styles.spikeBadge)}>
                {t('valueChainMap.monitoring.results.ownershipSpike')}
              </span>
            ) : null}
          </div>
        </div>

        <ChevronIcon
          className={clsx(styles.chevron, { [styles.chevronExpanded]: isExpanded })}
          aria-hidden="true"
        />
      </button>

      {isExpanded ? (
        <div className={styles.body}>
          <div className={styles.snapshotGrid}>
            <div className={styles.snapshotItem}>
              <span className={styles.snapshotLabel}>
                {t('valueChainMap.monitoring.results.ownershipUbo')}
              </span>
              <span className={styles.snapshotValue}>
                {profile.ultimate_beneficial_owner?.name || '—'}
                {profile.ultimate_beneficial_owner?.jurisdiction
                  ? ` · ${profile.ultimate_beneficial_owner.jurisdiction}`
                  : ''}
              </span>
            </div>
            <div className={styles.snapshotItem}>
              <span className={styles.snapshotLabel}>
                {t('valueChainMap.monitoring.results.ownershipDirectParent')}
              </span>
              <span className={styles.snapshotValue}>
                {profile.direct_parent?.name || '—'}
                {typeof profile.direct_parent?.ownership_stake_pct === 'number'
                  ? ` · ${profile.direct_parent.ownership_stake_pct}%`
                  : ''}
              </span>
            </div>
            <div className={styles.snapshotItem}>
              <span className={styles.snapshotLabel}>
                {t('valueChainMap.monitoring.results.ownershipCorporateGroup')}
              </span>
              <span className={styles.snapshotValue}>{profile.corporate_group || '—'}</span>
            </div>
            <div className={styles.snapshotItem}>
              <span className={styles.snapshotLabel}>
                {t('valueChainMap.monitoring.results.ownershipRelatedScanned')}
              </span>
              <span className={styles.snapshotValue}>{profile.related_entities_scanned ?? 0}</span>
            </div>
            <div className={styles.snapshotItem}>
              <span className={styles.snapshotLabel}>
                {t('valueChainMap.monitoring.results.ownershipRelatedFlagged')}
              </span>
              <span className={clsx(styles.snapshotValue, {
                [styles.snapshotValueFlagged]: (profile.related_entities_flagged || 0) > 0,
              })}
              >
                {profile.related_entities_flagged ?? 0}
              </span>
            </div>
          </div>

          {groupEntities.length ? (
            <div className={styles.groupPreviewList}>
              {groupEntities.map((groupEntity) => (
                <span
                  key={`${entity.unitId}-${groupEntity.name}`}
                  className={clsx(styles.groupChip, { [styles.groupChipFlagged]: groupEntity.risk_flag })}
                >
                  {groupEntity.name}
                  {groupEntity.role ? ` · ${groupEntity.role}` : ''}
                </span>
              ))}
            </div>
          ) : null}

          {(entity.alerts || []).length ? (
            <div className={styles.alertsList}>
              {entity.alerts?.map((alert) => (
                <div
                  key={`${entity.unitId}-${alert.title}`}
                  className={clsx(styles.alertItem, {
                    [styles.alertHigh]: alert.severity === 'high',
                    [styles.alertCritical]: alert.severity === 'critical',
                  })}
                >
                  <div className={styles.alertTitle}>{alert.title}</div>
                  <div className={styles.alertMeta}>
                    {alert.theme}
                    {alert.source ? ` · ${alert.source}` : ''}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

export default MonitoringOwnershipEntityCard;
